import { memo, useCallback, useMemo } from 'react';

import { useTrackCopiedContentCollectionLink } from '@air/analytics';
import { type ShortUrlResponse } from '@air/api/types';
import { Check, Copy } from '@air/next-icons';
import { Button } from '@air/primitive-button';

import { CopyField, type CopyFieldProps } from '~/components/UI/CopyField';
import { NEW_TAB } from '~/constants/urls';
import { useCopyLinkToClipboard } from '~/hooks/useCopyLinkToClipboard';

type ContentCollectionCopyLinkProps = {
  shortIdInfo: ShortUrlResponse;
  menuActions?: CopyFieldProps['menuActions'];
};

export const ContentCollectionCopyLink = memo(({ shortIdInfo, menuActions = [] }: ContentCollectionCopyLinkProps) => {
  const { trackCopiedContentCollectionLink } = useTrackCopiedContentCollectionLink();
  const { copyLinkToClipboard, copied } = useCopyLinkToClipboard();
  const shareUrl = `${window.location.origin}/d/${shortIdInfo.id}`;

  const _menuActions = useMemo(
    () => [
      {
        text: 'Preview link',
        action: () => {
          window.open(shareUrl, NEW_TAB);
        },
      },
      ...menuActions,
    ],
    [menuActions, shareUrl],
  );

  const onCopy = useCallback(() => {
    copyLinkToClipboard(shareUrl, trackCopiedContentCollectionLink);
  }, [copyLinkToClipboard, shareUrl, trackCopiedContentCollectionLink]);

  return (
    <div className="flex flex-col gap-2">
      <CopyField hideCopyButton url={shareUrl} menuActions={_menuActions} onCopy={trackCopiedContentCollectionLink} />
      <Button
        data-testid="CONTENT_COLLECTION_COPY_LINK_BUTTON"
        appearance="filled"
        className="w-full"
        color="blue"
        prefix={copied ? <Check className="size-6" /> : <Copy className="size-6" />}
        onClick={onCopy}
        size="extra-large"
      >
        {copied ? 'Link copied' : 'Copy link'}
      </Button>
    </div>
  );
});

ContentCollectionCopyLink.displayName = 'ContentCollectionCopyLink';
